import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { analyticsService } from "@/services/analytics/AnalyticsService";
import { Tier1Tracking } from "@/services/analytics/Tier1Tracking";

const CurationTroubleshootingPanel = () => {
  const { user } = useAuth();
  const [testEvents, setTestEvents] = useState<any[]>([]);

  const { data: curationData, isLoading, refetch } = useQuery({
    queryKey: ['curation-troubleshooting'],
    queryFn: async () => {
      // Get all curation events from the last 30 days
      const { data: events, error } = await supabase
        .from('user_activity_events')
        .select('*')
        .eq('event_category', 'curation')
        .gte('timestamp', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
        .order('timestamp', { ascending: false });
      
      if (error) {
        console.error('Error fetching curation events:', error);
        throw error;
      }
      
      // Get organization events that might be miscategorized
      const { data: orgEvents, error: orgError } = await supabase
        .from('user_activity_events')
        .select('*')
        .eq('event_category', 'organization')
        .gte('timestamp', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
        .order('timestamp', { ascending: false });
      
      if (orgError) {
        console.error('Error fetching organization events:', orgError);
        throw orgError;
      }
      
      return {
        curationEvents: events || [],
        organizationEvents: orgEvents || []
      };
    },
  });

  const testCurationEvent = async () => {
    try {
      if (!user) {
        setTestEvents(prev => [...prev, { type: 'error', error: 'No authenticated user found' }]);
        return;
      }

      console.log('Testing curation event using analyticsService...');
      
      // Use the AnalyticsService to track a curation event properly
      await analyticsService.trackEvent(
        'content_edited',
        'curation',
        {
          content_id: crypto.randomUUID(),
          fields_edited: ['title', 'description'],
          test: true,
          source: 'troubleshooting_panel'
        },
        user
      );

      // Force flush to ensure immediate processing
      await analyticsService.forceFlush();

      console.log('Test curation event sent successfully');
      setTestEvents(prev => [...prev, { 
        type: 'success', 
        data: { 
          event_type: 'content_edited',
          event_category: 'curation',
          timestamp: new Date().toISOString()
        } 
      }]);

      // Refresh the data to see the new event
      refetch();
      
    } catch (error) {
      console.error('Test event error:', error);
      setTestEvents(prev => [...prev, { type: 'error', error: String(error) }]);
    }
  };

  const testOrganizationEvent = async () => {
    try {
      if (!user) {
        setTestEvents(prev => [...prev, { type: 'error', error: 'No authenticated user found' }]);
        return;
      }

      console.log('[ANALYTICS DEBUG] Testing organization event...');
      
      // Test content organization tracking
      await Tier1Tracking.trackContentOrganized(
        crypto.randomUUID(), // fake content ID
        'tagged',
        crypto.randomUUID(), // fake tag ID
        'tag',
        user
      );

      console.log('[ANALYTICS DEBUG] Test organization event sent successfully');
      setTestEvents(prev => [...prev, { 
        type: 'success', 
        data: { 
          event_type: 'content_tagged',
          event_category: 'organization',
          timestamp: new Date().toISOString()
        } 
      }]);

      // Refresh the data to see the new event
      refetch();
      
    } catch (error) {
      console.error('[ANALYTICS DEBUG] Test organization event error:', error);
      setTestEvents(prev => [...prev, { type: 'error', error: String(error) }]);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Curation Activity Troubleshooting</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse">Loading curation data...</div>
        </CardContent>
      </Card>
    );
  }

  const { curationEvents = [], organizationEvents = [] } = curationData || {};

  // Analyze event types with proper typing
  const curationEventTypes: Record<string, number> = curationEvents.reduce((acc: Record<string, number>, event: any) => {
    acc[event.event_type] = (acc[event.event_type] || 0) + 1;
    return acc;
  }, {});

  const organizationEventTypes: Record<string, number> = organizationEvents.reduce((acc: Record<string, number>, event: any) => {
    acc[event.event_type] = (acc[event.event_type] || 0) + 1;
    return acc;
  }, {});

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-yellow-500" />
          Curation Activity Troubleshooting
        </CardTitle>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={testCurationEvent}
            disabled={!user}
          >
            Test Curation Event
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={testOrganizationEvent}
            disabled={!user}
          >
            Test Organization Event
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => refetch()}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-muted p-4 rounded-lg">
            <h4 className="font-medium mb-2">Curation Events (30 days)</h4>
            <p className="text-2xl font-mono">{curationEvents.length}</p>
          </div>
          <div className="bg-muted p-4 rounded-lg">
            <h4 className="font-medium mb-2">Organization Events (30 days)</h4>
            <p className="text-2xl font-mono">{organizationEvents.length}</p>
          </div>
          <div className="bg-muted p-4 rounded-lg">
            <h4 className="font-medium mb-2">Test Events Created</h4>
            <p className="text-2xl font-mono">{testEvents.length}</p>
          </div>
        </div>

        {/* Authentication Status */}
        {!user && (
          <div className="p-4 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">Authentication Required</h4>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              You must be signed in to test curation events. The events require a valid user ID.
            </p>
          </div>
        )}

        {/* Session Debug Info */}
        {user && (
          <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Session Status</h4>
            <div className="text-sm text-blue-700 dark:text-blue-300">
              <p>User ID: {user.id}</p>
              <p>Analytics Buffer: {analyticsService.getBufferStatus().bufferSize} events</p>
              <p>Session Initialized: {analyticsService.getBufferStatus().sessionInitialized ? 'Yes' : 'No'}</p>
              <p>Session ID: {analyticsService.getBufferStatus().sessionId || 'Not set'}</p>
            </div>
          </div>
        )}

        {/* Event Type Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium mb-3">Curation Event Types</h4>
            <div className="bg-muted p-3 rounded-lg">
              {Object.keys(curationEventTypes).length > 0 ? (
                <div className="space-y-2 text-sm">
                  {Object.entries(curationEventTypes).map(([type, count]) => (
                    <div key={type} className="flex justify-between">
                      <span>{type}:</span>
                      <span className="font-mono">{count as number}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No curation events found</p>
              )}
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-3">Organization Event Types (Top 10)</h4>
            <div className="bg-muted p-3 rounded-lg">
              <div className="space-y-2 text-sm">
                {Object.entries(organizationEventTypes)
                  .sort(([,a], [,b]) => (b as number) - (a as number))
                  .slice(0, 10)
                  .map(([type, count]) => (
                    <div key={type} className="flex justify-between">
                      <span className="truncate mr-2">{type}:</span>
                      <span className="font-mono">{count as number}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Events */}
        <div>
          <h4 className="font-medium mb-3">Recent Curation Events (Last 10)</h4>
          <div className="bg-muted p-3 rounded-lg max-h-60 overflow-y-auto">
            {curationEvents.slice(0, 10).length > 0 ? (
              <div className="space-y-2 text-xs">
                {curationEvents.slice(0, 10).map((event: any, index: number) => (
                  <div key={index} className="border-b border-border pb-2">
                    <div className="flex justify-between">
                      <span className="font-medium">{event.event_type}</span>
                      <span className="text-muted-foreground">
                        {new Date(event.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-muted-foreground">
                      User: {event.user_id?.slice(0, 8)}... | Data: {JSON.stringify(event.event_data)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No recent curation events found</p>
            )}
          </div>
        </div>

        {/* Test Results */}
        {testEvents.length > 0 && (
          <div>
            <h4 className="font-medium mb-3">Test Event Results</h4>
            <div className="bg-muted p-3 rounded-lg max-h-40 overflow-y-auto">
              <div className="space-y-2 text-xs">
                {testEvents.map((result, index) => (
                  <div key={index} className={`p-2 rounded ${result.type === 'success' ? 'bg-green-100 dark:bg-green-900/20' : 'bg-red-100 dark:bg-red-900/20'}`}>
                    <div className="font-medium">
                      {result.type === 'success' ? '✅ Success' : '❌ Error'}
                    </div>
                    <div className="text-muted-foreground">
                      {result.type === 'success' 
                        ? `Event: ${result.data.event_type} at ${new Date(result.data.timestamp).toLocaleTimeString()}` 
                        : `Error: ${result.error}`
                      }
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Updated Testing Guide */}
        <div className="mt-6 p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">Content Organization Tracking Added</h4>
          <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
            <li>• <strong>Added:</strong> Analytics tracking to tag operations (add/remove tags)</li>
            <li>• <strong>Added:</strong> Analytics tracking to collection operations (add/remove from collections)</li>
            <li>• <strong>Added:</strong> Analytics tracking to board operations (add/remove from boards)</li>
            <li>• <strong>Test:</strong> Use "Test Organization Event" button to verify tracking</li>
            <li>• <strong>Real Test:</strong> Try tagging content, adding to collections, or boards</li>
            <li>• All operations now include detailed console logging for debugging</li>
          </ul>
        </div>

        {/* Real Curation Activity Debugging */}
        <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg">
          <h4 className="font-medium text-orange-800 dark:text-orange-200 mb-2">Next Steps: Real Curation Tracking</h4>
          <ul className="text-sm text-orange-700 dark:text-orange-300 space-y-1">
            <li>• Test editing content titles/descriptions to see if trackContentEdited fires</li>
            <li>• Test adding notes to content items</li>
            <li>• Check if content organization actions (tagging, collections) are properly categorized</li>
            <li>• Verify that Tier1Tracking.trackContentEdited is being called from edit forms</li>
            <li>• Use the test button above to verify the fix is working</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default CurationTroubleshootingPanel;
